import { differenceInYears, subMonths } from 'date-fns';
import { isEmpty } from '@/lib/collections';
import {
    AlertTriangle, Pencil, Plus, Trash2, Upload, Download, ExternalLink,
    Motorbike, Bike, Zap, Scooter, Trees, Boxes, CheckCircle, Snowflake
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Swal, { type SweetAlertIcon } from 'sweetalert2';

import {
    PictoEspaceAssure, PictoConformite, PictoContrats, PictoSignatureElectronique,
    PictoConseils, PictoCheck, PictoFacilitePaiement
} from '@/assets/icons-april-fr/pictos';
import logoCertimat from '@/assets/images/avantages/logo_certimat.webp';
import APILink from '@/components/shared/api-link';
import AprilPicto from '@/components/shared/april-picto';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useConfirmResilHamon, useDeleteTiersPayeur, useTerminateAssuranceTransitoire, type ConfirmResilHamon } from '@/hooks/api/use-contract-actions';
import { useContractsData } from '@/hooks/api/use-contracts';
import { getCodedDocuments, hasWaitingDocuments, useContractDocuments } from '@/hooks/api/use-documents';
import { filterReceiptsOfContract, getLastReceipt, useContractReceipts, useReceiptsUnpaid } from '@/hooks/api/use-receipts';
import useIsPWA from '@/hooks/use-is-pwa';
import {
    getActionBlockedReason,
    getContractFractionnementLabel,
    getContractGarantiesOptions,
    hasOnlyContractApporteur43397,
    isActionAvailable,
    isAIMCOContract,
    isReductionAllianz,
    isWakamContract,
} from '@/lib/contracts';
import { diffInDays, formatDate, toDate } from '@/lib/dates';
import type { Contract, Garantie, TiersPayeur, Vehicule } from '@/types/api/contract';
import { tiersPayeurRelation } from '@/lib/options/select-options';

import Documents from '@/pages/contract/detail-contract/documents';
import Flotte from '@/pages/contract/detail-contract/flotte';

const getProductIcon = (codeProduit?: string) => {
    if (!codeProduit) return Motorbike;
    const code = codeProduit.toUpperCase();
    if (code.includes('VAE') || code.includes('VELO')) return Bike;
    if (code.includes('MAE') && code.includes('NVEI')) return Zap;
    if (code.includes('NVEI') || code.includes('TROT')) return Scooter;
    if (code.includes('QUAD') || code.includes('VERTE') || code.includes('QUA') || code.includes('MVE')) return Trees;
    if (code.includes('LIV') || code.includes('LIVRAISON')) return Boxes;
    return Motorbike;
};

const GarantiesCard = ({ garanties, vehicule }: { garanties: Garantie[]; vehicule: Vehicule }) => {
    /* Le PASS renvoie les garanties dans l'ordre d'affichage Belair (ORDER BY B_AFFICHE) :
       on ne re-trie pas. type = B_NATOPT : 'O' = option, sinon garantie. */
    const filteredGaranties = garanties.filter(
        (g) => !['FRAIS_GESTION', 'FRAIS_FRAC', 'FRAIS_VARIATION'].includes(g.code_famille ?? '') && !g.code?.startsWith('MD') && !isReductionAllianz(g)
    );
    const sections = [
        { titre: 'Garanties', items: filteredGaranties.filter((g) => g.type !== 'O') },
        { titre: 'Options', items: filteredGaranties.filter((g) => g.type === 'O') },
    ].filter((s) => s.items.length > 0);

    return (
        <Card data-tutorial="contract-garanties">
            <CardHeader className="pb-6 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                    <AprilPicto icon={PictoConformite} size="md" />
                    Mes garanties et options
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
                {sections.map((section) => (
                    <div key={section.titre}>
                        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{section.titre}</p>
                        <div className="grid gap-3">
                            {section.items.map((g) => (
                                <div key={g.code} className="flex items-center p-4 bg-muted/50 rounded-lg transition-colors duration-200 hover:bg-primary/10">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-success text-white shrink-0 mr-3">
                                        <CheckCircle className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 sm:flex sm:items-center sm:justify-between">
                                        <p className="font-medium">{g.libelle}</p>
                                        {vehicule.codeProduit !== '20MOT_GAN' && vehicule.libelleProduit !== 'AUTO' && g.franchise && (
                                            <p className="text-sm text-muted-foreground mt-0.5 sm:mt-0">{g.franchise}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};

export interface DetailContractProps {
    contract: Contract;
}

const DetailContract = ({ contract }: DetailContractProps) => {
    const id = contract.id;
    const hasOnlyApporteur43397 = hasOnlyContractApporteur43397(useContractsData());
    const isWakam = isWakamContract(contract);
    const souscripteur = contract.souscripteur || {};
    const tiersPayeur = contract.tiers_payeur;
    const tiersPayeurData = (tiersPayeur ?? {}) as TiersPayeur;
    const vehicule = contract.vehicule || {};
    const old_vehicule = contract.old_vehicule || {};
    const garanties = getContractGarantiesOptions(contract) ?? [];
    const hamon = contract.hamon;
    const police = contract.numeroPolice;
    const uploadAppLink = contract.upload_app_link;
    const statutCode = contract.situation?.statutCode;
    const isAIMCO = isAIMCOContract(contract);
    const prelevement = contract.prelevement;
    const fractionnement = getContractFractionnementLabel(contract);
    const crm = vehicule.crm;
    const canClaimMobilite = isActionAvailable(contract, 'garantie_mobilite');
    const mobiliteBlockedReason = getActionBlockedReason(contract, 'garantie_mobilite');
    const is_eligible_hivernage = contract.is_eligible_hivernage || false;

    const documentsQuery = useContractDocuments(contract.id);
    const documents = getCodedDocuments(documentsQuery.data) ?? [];
    const has_waiting_doc = hasWaitingDocuments(documentsQuery.data);
    const receiptsUnpaidQuery = useReceiptsUnpaid();
    const receipts_unpaid = filterReceiptsOfContract(receiptsUnpaidQuery.data, id);
    const receiptsQuery = useContractReceipts(id);
    const quittance = getLastReceipt(receiptsQuery.data);

    const { mutate: postConfirmResilHamon } = useConfirmResilHamon();
    const { mutate: postTerminateAssuranceTransitoire } = useTerminateAssuranceTransitoire();
    const { mutate: postDeleteTiersPayeur } = useDeleteTiersPayeur();
    const submitDeleteTiersPayeur = () => postDeleteTiersPayeur({ contractId: id });

    const isPWA = useIsPWA();
    const isContractApporteur43397 = String(contract?.apporteur1) === '43397';
    const productCode = (contract?.vehicule?.codeProduit || '').toUpperCase();
    const isContractNVEIorVAE = productCode.includes('NVEI') || productCode.includes('VAE') || productCode.includes('TROT') || productCode.includes('VELO');
    const isFleet = !!contract?.flotte;
    /* situation.dateSituation = date d'effet de la dernière pièce du contrat (B_DATESIT côté PASS) */
    const isDernierePieceRecente = contract?.situation?.dateSituation && diffInDays(new Date(), contract.situation.dateSituation) < 30;

    const documentsSigned = documents.filter((document) => document.download_link !== null);
    const dateAffaireNouvelle = toDate(contract.situation?.dateAffaireNouvelle);
    const sixMonthsAgo = subMonths(new Date(), 6);

    const hasMemo = quittance?.memo_link && !isEmpty(vehicule?.immatriculation) && !['6', '5', 'I'].includes(statutCode ?? '') && !isContractNVEIorVAE;
    /* La garantie mobilité n'existe que sur les produits NVEI : on aligne le test sur celui du
       back (strstr sur codeProduit) plutôt que sur l'égalité 7311_NVEI, sinon les MAE_NVEI
       seraient exclus à tort. L'éligibilité réelle reste portée par `actions.garantie_mobilite`. */
    const hasGarantieMobilite = !isFleet && productCode.includes('NVEI');
    /* Ancien bouton de la sidebar (supprimée à la refonte) : mêmes conditions que la route /hivernage */
    const hasHivernage = isContractApporteur43397 && is_eligible_hivernage && !isWakam && !isFleet;
    const hasAttestation = quittance?.attestation_assurance_link && !['6', '5', 'I'].includes(statutCode ?? '');
    const VehiculeIcon = getProductIcon(vehicule?.codeProduit);

    const handleClickInfo = () => {
        Swal.fire({
            icon: 'question',
            title: 'Le saviez-vous ?',
            text: 'Lors de la résiliation, votre ancien assureur doit vous faire parvenir un courrier de confirmation (courrier libre / email / relevé de sinistres) justifiant l\'arrêt de votre contrat',
            showConfirmButton: true,
            confirmButtonText: 'Terminer',
            showCloseButton: true
        });
    };

    const handleConfirmHamonChange = (value: ConfirmResilHamon) => {
        postConfirmResilHamon({ contractId: id, confirmResil: value });
    };

    const handleClickInfoAssTransitoire = () => {
        Swal.fire({
            icon: '' as unknown as SweetAlertIcon,
            title: '',
            text: 'Ce véhicule bénéficie des mêmes garanties et options que celles de votre nouveau véhicule, uniquement s\'il est mis en circulation pour des essais en vue de la vente et pour une durée maximale de 30 jours à compter du jour où l\'assurance a été reportée sur le nouveau véhicule.',
            showConfirmButton: true,
            confirmButtonText: 'Ok',
            showCloseButton: true
        });
    };

    const handleTerminateAssuranceTransitoire = () => {
        Swal.fire({
            title: 'Confirmation',
            text: 'Confirmez-vous avoir vendu votre ancien véhicule ? Cela mettra fin à l\'assurance transitoire.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Oui, je confirme',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                postTerminateAssuranceTransitoire({ contractId: id });
            }
        });
    };

    const getStatusVariant = (statutCode?: string) => {
        switch (statutCode) {
            case '1': return 'success' as const;
            case '6':
            case '5': return 'destructive' as const;
            case 'I':
            case '2': return 'warning' as const;
            default: return 'secondary' as const;
        }
    };

    const canModifyContract =
        !isFleet &&
        differenceInYears(new Date(), toDate(contract?.situation?.dateAffaireNouvelle)) >= 1 &&
        contract?.avenant_link !== '' && contract?.avenant_link &&
        hasOnlyApporteur43397 &&
        (contract?.vehicule?.codeProduit !== '7311_VAE' && contract?.vehicule?.codeProduit !== '7311_NVEI');

    if (documentsQuery.isLoading || receiptsUnpaidQuery.isLoading || receiptsQuery.isLoading) return <SmallSpinner />;

    return (
        <div className="space-y-6 mt-4">
            {/* Hero Section */}
            <div data-tutorial="contract-hero" className="relative overflow-hidden rounded-2xl bg-primary p-6 text-white">
                <div className="absolute -top-1/2 -right-[10%] w-[400px] h-[400px] bg-white/10 rounded-full" />
                <div className="relative z-10">
                    <p className="text-xs sm:text-sm opacity-90 mb-1">Contrat N°{police}</p>
                    <h1 className="text-xl sm:text-3xl font-semibold mb-6">
                        {vehicule.marque} {vehicule.modele}
                    </h1>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:[grid-template-columns:repeat(auto-fit,minmax(180px,1fr))] gap-3 sm:gap-6">
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">Souscripteur</p>
                            <p className="text-sm sm:text-lg font-semibold">{souscripteur.nom}</p>
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">N° client</p>
                            <p className="text-sm sm:text-lg font-semibold">{souscripteur.id || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">Échéance principale</p>
                            <p className="text-sm sm:text-lg font-semibold">{formatDate(contract.situation?.dateEcheance, 'dd/MM') || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm opacity-80 mb-0.5">Fractionnement</p>
                            <p className="text-sm sm:text-lg font-semibold">{fractionnement || '—'}</p>
                        </div>
                        {/* Les produits NVEI / VAE ne sont pas soumis au bonus-malus : pas de CRM à afficher. */}
                        {!isContractNVEIorVAE && !isWakam && crm && (
                            <div className={String(crm).includes('Contrat non soumis') ? 'col-span-2 lg:col-span-full' : ''}>
                                <p className="text-xs sm:text-sm opacity-80 mb-0.5">Bonus en cours</p>
                                <p className="text-sm sm:text-lg font-semibold">{crm}</p>
                            </div>
                        )}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-3">
                        <Badge variant={getStatusVariant(contract.situation?.statutCode)} className={isPWA ? "text-sm px-6 py-1.5" : "text-xs sm:text-sm px-2.5 sm:px-4 py-1"}>
                            <span className="hidden sm:inline">Situation du contrat : </span>{contract.situation?.statut}
                        </Badge>
                        {canModifyContract && (
                            <Button asChild size="sm" variant="outline" className="!border-white !text-white hover:!bg-white/20 hover:!border-white cursor-pointer text-xs sm:text-sm">
                                <a href={contract.avenant_link} target="_blank" rel="noopener noreferrer">
                                    <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    <span className="hidden sm:inline">Modifier mon contrat</span>
                                    <span className="sm:hidden">Modifier</span>
                                </a>
                            </Button>
                        )}
                        {hasHivernage && (
                            <Button asChild size="sm" variant="outline" className="!border-white !text-white hover:!bg-white/20 hover:!border-white cursor-pointer text-xs sm:text-sm">
                                <Link to={`/mes-contrats/mon-contrat/${id}/hivernage`}>
                                    <Snowflake className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    Gérer Ma période hivernale
                                </Link>
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Bandeau cotisation à régler */}
            {!isFleet && receipts_unpaid.map((r, i) => (
                <Alert key={i} className="bg-destructive/10 text-destructive border-destructive/30 items-center [&>svg]:translate-y-0 has-[>svg]:gap-x-4">
                    <AlertTriangle className="!h-6 !w-6 text-destructive" />
                    <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <span className="font-medium">
                            <span className="text-destructive font-bold">Vous avez une cotisation à régler !</span> Contrat : {r.numeroPolice} - <span className="font-bold">{r.solde}€</span> restant
                        </span>
                        {r.pay_link ? (
                            <Button asChild size="sm" variant="outline" className="shrink-0 cursor-pointer">
                                <a target="_blank" rel="noopener noreferrer" href={`${r.pay_link}?url_return=${window.location.href}`}>
                                    <AprilPicto icon={PictoFacilitePaiement} size="sm" />
                                    Payer ma cotisation
                                </a>
                            </Button>
                        ) : (
                            <Badge variant="secondary">
                                {(r.pay_link_reason?.[0] ?? '').replace('_', ' ')}
                            </Badge>
                        )}
                    </AlertDescription>
                </Alert>
            ))}

            {/* Action Cards - petits blocs côte à côte */}
            {!isFleet && (has_waiting_doc || hasAttestation || hasMemo || hasGarantieMobilite) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

                    {has_waiting_doc && (
                        <Card className="transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-purple-400 flex flex-col">
                            <CardContent className="pt-6 text-center flex flex-col flex-1">
                                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-purple-100 mx-auto mb-4">
                                    <AprilPicto icon={PictoContrats} size="lg" />
                                </div>
                                <p className="text-sm font-semibold mb-1">Documents à envoyer</p>
                                <p className="text-sm text-muted-foreground mb-4">Vous avez des documents en attente d'envoi pour finaliser votre dossier.</p>
                                <div className="mt-auto">
                                    <APILink href={uploadAppLink ?? ''} className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md border border-purple-300 text-purple-700 hover:bg-primary-hover hover:!text-white transition-all duration-300 ease-in-out cursor-pointer no-underline">
                                        <Upload className="w-4 h-4" />
                                        Envoyer mes documents
                                    </APILink>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {(hamon?.id && hamon?.confirm_resil !== 'T' && dateAffaireNouvelle.getTime() > sixMonthsAgo.getTime()) ? (
                        <Card className="border-2 border-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary">
                            <CardContent className="pt-6 text-center">
                                <p className="text-sm font-semibold text-destructive mb-1">
                                    Vous avez souscrit par le biais de la loi&nbsp;Hamon&nbsp;!
                                </p>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Avez-vous reçu la confirmation de la résiliation de votre ancienne assurance ?
                                    <button
                                        onClick={handleClickInfo}
                                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-sm ml-2 cursor-pointer"
                                        aria-label="Informations sur la loi Hamon"
                                    >?</button>
                                </p>
                                <div className="flex items-center justify-center gap-3">
                                    <Button size="sm" className="cursor-pointer transition-all duration-300 ease-in-out text-white" onClick={() => handleConfirmHamonChange('T')}>Oui</Button>
                                    <Button variant="outline" size="sm" className="cursor-pointer" onClick={() => handleConfirmHamonChange('F')}>Non</Button>
                                    <Button variant="outline" size="sm" className="cursor-pointer" onClick={() => handleConfirmHamonChange('help')}>J'ai besoin d'aide</Button>
                                </div>
                            </CardContent>
                        </Card>
                    ) : hamon?.confirm_resil === 'T' ? (
                        <Card className="border-2 border-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary">
                            <CardContent className="pt-6 text-center">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <AprilPicto icon={PictoCheck} size="md" />
                                    <p className="text-sm font-semibold text-success">
                                        Loi Hamon : Confirmation de résiliation reçue !
                                    </p>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    Merci d'avoir confirmé la réception de la résiliation de votre ancienne assurance.
                                </p>
                            </CardContent>
                        </Card>
                    ) : null}

                    {/* Mémo Véhicule Assuré */}
                    {hasMemo && (() => {
                        const ProductIcon = getProductIcon(vehicule?.codeProduit);
                        return (
                            <Card className="border-2 border-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary flex flex-col">
                                <CardContent className="pt-6 text-center flex flex-col flex-1">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mx-auto mb-4">
                                        <ProductIcon className="w-6 h-6 text-primary" />
                                    </div>
                                    <p className="text-sm font-semibold mb-1">Télécharger mon Mémo Véhicule</p>
                                    <p className="text-sm text-muted-foreground mb-4">Toutes les infos essentielles de votre contrat en un PDF.</p>
                                    <div className="mt-auto">
                                        <APILink href={quittance?.memo_link ?? ''} className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md border-2 border-primary text-primary hover:border-foreground hover:text-foreground transition-all duration-300 ease-in-out cursor-pointer no-underline">
                                            <Download className="w-4 h-4" />
                                            Télécharger le mémo
                                        </APILink>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })()}

                    {/* Attestation d'assurance */}
                    {hasAttestation && (
                        <Card className="border-2 border-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary flex flex-col">
                            <CardContent className="pt-6 text-center flex flex-col flex-1">
                                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mx-auto mb-4">
                                    <AprilPicto icon={PictoContrats} size="lg" />
                                </div>
                                <p className="text-sm font-semibold mb-1">Télécharger mon attestation d'assurance</p>
                                <p className="text-sm text-muted-foreground mb-4">Votre attestation d'assurance pour votre contrat 2-roues.</p>
                                <div className="mt-auto">
                                    <APILink href={quittance?.attestation_assurance_link ?? ''} className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary-hover transition-all duration-300 ease-in-out cursor-pointer no-underline">
                                        <Download className="w-4 h-4" />
                                        Télécharger
                                    </APILink>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Garantie mobilité (produits NVEI / trottinette) */}
                    {hasGarantieMobilite && (
                        <Card className="border-2 border-transparent transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-primary flex flex-col">
                            <CardContent className="pt-6 text-center flex flex-col flex-1">
                                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mx-auto mb-4">
                                    <Scooter className="w-6 h-6 text-primary" />
                                </div>
                                <p className="text-sm font-semibold mb-1">Ma garantie mobilité</p>
                                <p className="text-sm text-muted-foreground mb-4">Vous n'avez pas pu utiliser votre trottinette ? Faites-vous rembourser votre trajet alternatif.</p>
                                <div className="mt-auto">
                                    {canClaimMobilite ? (
                                        <Button asChild size="sm" className="cursor-pointer text-white">
                                            <Link to={`/mes-contrats/mon-contrat/${id}/ma-garantie-mobilite`}>
                                                <Scooter className="w-4 h-4" />
                                                J'active ma garantie mobilité
                                            </Link>
                                        </Button>
                                    ) : (
                                        <Badge variant="secondary">
                                            {mobiliteBlockedReason}
                                        </Badge>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Souscripteur / Assuré */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AprilPicto icon={PictoEspaceAssure} size="md" />
                        Souscripteur / Assuré
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Assuré</p>
                            <p className="font-medium">{souscripteur.nom}</p>
                        </div>
                        {!isWakam && (
                            <div>
                                <p className="text-sm text-muted-foreground mb-0.5">Date de naissance</p>
                                <p className="font-medium">{formatDate(souscripteur.naissance) || '—'}</p>
                            </div>
                        )}
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Adresse postale</p>
                            <p className="font-medium">{`${souscripteur.adresse?.rue1} ${souscripteur.adresse?.rue2 || ''} ${souscripteur.adresse?.rue3 || ''}`}</p>
                            {isContractApporteur43397 && statutCode === '1' && !isAIMCO && !isWakam && dateAffaireNouvelle.getTime() < Date.now() && (
                                <Link to={`/mes-contrats/mon-contrat/${id}/je-demenage`} className="text-sm text-primary hover:underline no-underline flex items-center gap-1 mt-1">
                                    <Pencil className="w-3.5 h-3.5" /> Modifier
                                </Link>
                            )}
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Code postal - Ville</p>
                            <p className="font-medium">{`${souscripteur.adresse?.codePostal} ${souscripteur.adresse?.ville}`}</p>
                            {isContractApporteur43397 && statutCode === '1' && !isAIMCO && !isWakam && dateAffaireNouvelle.getTime() < Date.now() && (
                                <Link to={`/mes-contrats/mon-contrat/${id}/je-demenage`} className="text-sm text-primary hover:underline no-underline flex items-center gap-1 mt-1">
                                    <Pencil className="w-3.5 h-3.5" /> Modifier
                                </Link>
                            )}
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Numéro de téléphone</p>
                            <p className="font-medium">{souscripteur.phone || '—'}</p>
                            {isContractApporteur43397 && (
                            <Link to="/mon-profil/je-change-mes-informations" className="text-sm text-primary hover:underline no-underline flex items-center gap-1 mt-1">
                                <Pencil className="w-3.5 h-3.5" /> Modifier
                            </Link>
                            )}
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Adresse email</p>
                            <p className="font-medium">{souscripteur.email || '—'}</p>
                            {isContractApporteur43397 && (
                            <Link to="/mon-profil/je-change-mes-informations" className="text-sm text-primary hover:underline no-underline flex items-center gap-1 mt-1">
                                <Pencil className="w-3.5 h-3.5" /> Modifier
                            </Link>
                            )}
                        </div>
                        {!isFleet && (
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Tiers payeur</p>
                            <p className="font-medium">
                                {!isEmpty(tiersPayeur) ? (
                                    <span>
                                        {tiersPayeurData.nom}{` ${tiersPayeurData.prenom || ''}`}{' '}
                                        {tiersPayeurData.relation && (
                                            <>- {tiersPayeurRelation.find((relation) => relation.code === tiersPayeurData.relation)?.libelle}
                                                {tiersPayeurData.relation === 'A' && tiersPayeurData.raison_autre && ` : ${tiersPayeurData.raison_autre}`}
                                            </>
                                        )}
                                    </span>
                                ) : 'Pas de tiers payeur'}
                            </p>
                            <div className="flex items-center gap-3 mt-1">
                                {!isEmpty(tiersPayeur) ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (window.confirm('Êtes-vous sûr(e) de vouloir supprimer le tiers payeur de votre contrat ?'))
                                                    submitDeleteTiersPayeur();
                                            }}
                                            className="text-sm text-destructive hover:underline flex items-center gap-1 cursor-pointer"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> Supprimer
                                        </button>
                                        <Link to={`/mes-contrats/mon-contrat/${id}/mon-tiers-payeur`} className="text-sm text-primary hover:underline no-underline flex items-center gap-1">
                                            <Pencil className="w-3.5 h-3.5" /> Modifier
                                        </Link>
                                    </>
                                ) : (
                                    <Link to={`/mes-contrats/mon-contrat/${id}/mon-tiers-payeur`} className="text-sm text-primary hover:underline no-underline flex items-center gap-1">
                                        <Plus className="w-3.5 h-3.5" /> Ajouter
                                    </Link>
                                )}
                            </div>
                        </div>
                        )}
                        {!isFleet && (
                        <div>
                            <p className="text-sm text-muted-foreground mb-0.5">Information de Prélèvement</p>
                            <p className="font-medium">{`Prélèvement : ${prelevement ? `Oui (${fractionnement})` : 'Non'}`}</p>
                            {!prelevement && statutCode === '1' && (
                                <Link to={`/mes-contrats/mon-contrat/${id}/je-passe-en-prelevement`} className="text-sm text-primary hover:underline no-underline flex items-center gap-1 mt-1">
                                    <Pencil className="w-3.5 h-3.5" /> Modifier
                                </Link>
                            )}
                        </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Véhicule assuré */}
            {contract.flotte ? (
                <Flotte className="loader" contract={contract} />
            ) : (
                <>
                    <Card data-tutorial="contract-vehicule">
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <VehiculeIcon className="w-5 h-5 text-primary" />
                                Véhicule assuré
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Véhicule assuré</p>
                                    <p className="font-medium">{`${vehicule.marque} ${vehicule.modele} ${vehicule.cylindree || ''}`}</p>
                                </div>
                                <div>
                                    {/* NVEI / VAE ne sont pas immatriculés : le back expose le numéro de série
                                        (B_NSERIE) sous la clé VIN du véhicule. */}
                                    <p className="text-sm text-muted-foreground mb-0.5">{isContractNVEIorVAE ? 'Numéro de série' : 'Immatriculation'}</p>
                                    <p className="font-medium">{(isContractNVEIorVAE ? vehicule.VIN : vehicule.immatriculation) || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Usage déclaré</p>
                                    <p className="font-medium">{vehicule.usage || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Zone de stationnement</p>
                                    <p className="font-medium">{vehicule.lieuStationnement || '—'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Assurance transitoire */}
                    {(old_vehicule?.immatriculation && !vehicule.vente_old_vehicule && toDate(old_vehicule.date_fin_piece).getTime() > Date.now()) && (
                        <Card className="border-warning/30">
                            <CardHeader className="pb-6 border-b">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5 text-warning" />
                                    Assurance transitoire
                                    <button
                                        onClick={handleClickInfoAssTransitoire}
                                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-sm cursor-pointer"
                                        aria-label="Informations sur l'assurance transitoire"
                                    >?</button>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-0.5">Véhicule assuré</p>
                                        <p className="font-medium">{`${old_vehicule.marque} ${old_vehicule.modele}`}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-0.5">Immatriculation</p>
                                        <p className="font-medium">{old_vehicule.immatriculation}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-0.5">Date d'effet</p>
                                        <p className="font-medium">{formatDate(old_vehicule.date_effet_piece)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-0.5">Date de fin</p>
                                        <p className="font-medium">{formatDate(old_vehicule.date_fin_piece)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-0.5">Usage</p>
                                        <p className="font-medium">Essais en vue de la vente</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-0.5">Statut</p>
                                        <Badge variant="warning">En cours</Badge>
                                    </div>
                                </div>
                                <div className="pt-12">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="cursor-pointer"
                                        onClick={(e) => { e.preventDefault(); handleTerminateAssuranceTransitoire(); }}
                                    >
                                        Oui, j'ai vendu ce véhicule
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Garanties et options */}
                    <GarantiesCard garanties={garanties} vehicule={vehicule} />
                </>
            )}

            {/* Mes documents */}
            <Documents documents={documents} uploadAppLink={uploadAppLink} isContractApporteur43397={isContractApporteur43397} />

            {/* Documents signés */}
            <Card>
                <CardHeader className="pb-6 border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <AprilPicto icon={PictoSignatureElectronique} size="md" />
                        Mes documents signés électroniquement
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                    {documentsSigned.length > 0 ? (
                        <div className="grid gap-3">
                            {documentsSigned.map((d) => (
                                <div key={d.code} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg transition-all duration-200 hover:bg-primary/10 hover:translate-x-1">
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 shrink-0">
                                            <AprilPicto icon={PictoContrats} size="md" />
                                        </div>
                                        <div>
                                            <p className="font-medium">{d.libelle}</p>
                                            <p className="text-sm text-muted-foreground">
                                                Signé le : {formatDate(d.dateReception)}
                                            </p>
                                        </div>
                                    </div>
                                    <APILink href={d.download_link ?? ''} target="_blank" download>
                                        <Button variant="outline" size="sm" className="cursor-pointer">
                                            <AprilPicto icon={PictoContrats} size="sm" />
                                            Télécharger
                                        </Button>
                                    </APILink>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground">Pas de documents signés électroniquement.</p>
                    )}
                </CardContent>
            </Card>

            {/* Carte grise */}
            {isContractApporteur43397 && !isContractNVEIorVAE && !isFleet && isDernierePieceRecente && (
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <AprilPicto icon={PictoConseils} size="md" />
                            Besoin d'aide ?
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-6">
                            <div>
                                <p className="font-semibold mb-2">Vous devez mettre votre carte grise à votre nom ?</p>
                                <p className="text-sm text-muted-foreground mb-6">Grâce à Certimat, partenaire 100% sécurisé, mettez votre carte grise à jour simplement.</p>
                                <div className="space-y-1.5 mb-12">
                                    <div className="flex items-center gap-2 text-sm">
                                        <AprilPicto icon={PictoCheck} size="sm" className="shrink-0" />
                                        <span>Traitement immédiat</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <AprilPicto icon={PictoCheck} size="sm" className="shrink-0" />
                                        <span>Démarche en ligne</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <AprilPicto icon={PictoCheck} size="sm" className="shrink-0" />
                                        <span>Paiement de vos taxes jusqu'en 10 fois</span>
                                    </div>
                                </div>
                                <Button asChild variant="outline" className="cursor-pointer">
                                    <a href="https://certimat.fr/prescripteurs?partner=3871" target="_blank" rel="noopener noreferrer">
                                        <ExternalLink className="w-4 h-4" />
                                        Faire ma carte grise
                                    </a>
                                </Button>
                            </div>
                            <div className="hidden sm:flex flex-1 items-center justify-center">
                                <img src={logoCertimat} alt="Certimat" className="w-56" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

        </div>
    );
};

export default DetailContract;
